// The slide's coordinate space, for the parts of the canvas renderer that need
// the numbers rather than the CSS variables — a chart has to compute a viewBox
// and place its own marks.
//
// These mirror SLIDE, FRAME and the chart constants in
// src/company-workspace/decks/theme.mjs, which the .pptx exporter reads. The
// UI cannot import from the server tree (separate tsconfig root, and the server
// has no business in the bundle), so the numbers appear twice and
// test/workspace-deck-theme.mjs proves they still agree. A chart drawn to a
// different plot box than the exporter's is the one kind of drift a reader
// notices immediately, because the marks move.
export const SLIDE = { width: 1280, height: 720 } as const;

export const FRAME = {
  marginX: 88,
  marginTop: 72,
  marginBottom: 64,
  titleHeight: 46,
  leadHeight: 30,
  gap: 28,
  takeawayHeight: 44,
  sourceHeight: 20,
  footerGap: 14,
  radius: 12,
} as const;

export const CHART = {
  gridLines: 4,
  legendHeight: 30,
  axisHeadroom: 14,
  categoryBand: 34,
  valueGutter: 76,
  categoryGutter: 150,
  maxBarThickness: 46,
  maxColumnWidth: 160,
  // A horizontal bar labels its own data end, so the plot stops short of the
  // frame edge to leave room for the widest of those labels.
  barLabelGutter: 96,
} as const;

export const STACKED_TYPES = ['stacked-column', 'stacked-bar'];
export const HORIZONTAL_TYPES = ['bar', 'stacked-bar'];

export const CONTENT_WIDTH = SLIDE.width - FRAME.marginX * 2;

type Bands = { title?: boolean; lead?: boolean; takeaway?: boolean; source?: boolean };

/** The box a layout gets after the shared bands have taken theirs. */
export function contentBox({ title, lead, takeaway, source }: Bands) {
  const y =
    FRAME.marginTop +
    (title ? FRAME.titleHeight : 0) +
    (lead ? FRAME.leadHeight : 0) +
    (title || lead ? FRAME.gap : 0);
  const bottom =
    SLIDE.height -
    FRAME.marginBottom -
    (takeaway ? FRAME.takeawayHeight : 0) -
    (source ? FRAME.sourceHeight : 0) -
    (takeaway || source ? FRAME.footerGap : 0);
  return { x: FRAME.marginX, y, width: CONTENT_WIDTH, height: bottom - y };
}

/** Shared with the exporter so an axis and a data label cannot disagree. */
export function formatValue(
  value: number | null,
  { valueFormat = 'number', unit }: { valueFormat?: string; unit?: string } = {},
) {
  if (value === null || value === undefined) return '';
  const trim = (n: number) => String(Math.round(n * 100) / 100);
  if (valueFormat === 'percent') return `${trim(value)}%`;
  const abs = Math.abs(value);
  let text = trim(value);
  if (abs >= 1e9) text = `${trim(value / 1e9)}B`;
  else if (abs >= 1e6) text = `${trim(value / 1e6)}M`;
  else if (abs >= 1e4) text = `${trim(value / 1e3)}k`;
  return unit ? `${text} ${unit}` : text;
}

// Ticks a person would choose: 1, 2, 2.5 or 5 times a power of ten. Dividing
// the range into four equal parts instead labels an axis 9.85, 19.7, 29.55.
export function niceScale(min: number, max: number, target = CHART.gridLines) {
  const raw = (max - min || 1) / target;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const normal = raw / magnitude;
  const step =
    (normal <= 1 ? 1 : normal <= 2 ? 2 : normal <= 2.5 ? 2.5 : normal <= 5 ? 5 : 10) * magnitude;
  const low = Math.floor(min / step) * step;
  const high = Math.ceil(max / step) * step;
  return { min: low, max: high, span: high - low || 1, step };
}

type Chartish = {
  type: string;
  categories: string[];
  series: { values: (number | null)[] }[];
};

export function chartScale(chart: Chartish) {
  const values = STACKED_TYPES.includes(chart.type)
    ? chart.categories.map((_, at) =>
        chart.series.reduce((sum, one) => sum + (one.values[at] ?? 0), 0),
      )
    : chart.series.flatMap((one) => one.values.filter((value): value is number => value !== null));
  const seen = values.length ? values : [0];
  // A bar or a column is read by comparing lengths, so its axis has to include
  // zero — truncating it overstates every difference. A line is read by its
  // slope, so forcing zero flattens the change it was drawn to show.
  const zeroed = chart.type !== 'line';
  const low = zeroed ? Math.min(0, ...seen) : Math.min(...seen);
  const high = zeroed ? Math.max(0, ...seen) : Math.max(...seen);
  if (zeroed) return niceScale(low, high);
  // The step comes from the data's own span; the padding then snaps out to it.
  const { step } = niceScale(low, high);
  const pad = (high - low || 1) * 0.15;
  const min = Math.floor((low - pad) / step) * step;
  const max = Math.ceil((high + pad) / step) * step;
  return { min, max, span: max - min || 1, step };
}

export function ticksOf(scale: { min: number; max: number; step: number }) {
  const ticks: number[] = [];
  for (let value = scale.min; value <= scale.max + scale.step / 1000; value += scale.step)
    ticks.push(Math.round(value / scale.step) * scale.step);
  return ticks;
}

/** A line spans the plot: first point on the axis, last at the right edge. */
export function linePointX(index: number, count: number, plot: { x: number; width: number }) {
  return count === 1 ? plot.x + plot.width / 2 : plot.x + (index / (count - 1)) * plot.width;
}

export const CHART_CATEGORICAL = ['#e16c26', '#2a75ba', '#00986c', '#9a2a95'] as const;
export const CHART_SEQUENTIAL = ['#e48b5d', '#d1611b', '#ab4600', '#783305'] as const;
